import {
  DesktopIntegrationStatusEvent,
  DesktopIntegrationStatusEventType,
  DesktopProfileUpdateStatusEvent,
  DesktopProfileUpdateStatusEventType,
} from '@repo/utils/types';
import {
  DeviceIntegrationProvider,
  SyncDeviceIntegrationOpts,
} from '@repo/utils/models/Integration';

export const electronPlatformToAPIPlatform = (platform) => {
  switch (platform) {
    case 'twitter':
      return 'twitter-platforms';
    default:
      return platform;
  }
};

export const apiPlatformToElectronPlatform = (platform) => {
  switch (platform) {
    case 'twitter-platforms':
      return 'twitter';
    default:
      return platform;
  }
};

// Action creators
export const setIntegrationAuthorization = (payload) => ({
  type: 'SET_INTEGRATION_AUTHORIZATION',
  payload,
});
const integrationStartedSync = (payload) => ({ type: 'INTEGRATION_STARTED_SYNC', payload });
const integrationCompletedSync = (payload) => ({ type: 'INTEGRATION_COMPLETED_SYNC', payload });
const integrationSyncProgress = (payload) => ({ type: 'INTEGRATION_SYNC_PROGRESS', payload });
const integrationRemoved = (payload) => ({ type: 'DEVICE_INTEGRATION_REMOVED', payload });
const integrationErrored = (payload) => ({ type: 'DEVICE_INTEGRATION_ERRORED', payload });
const integrationSetupAborted = (payload) => ({
  type: 'DEVICE_INTEGRATION_SETUP_ABORTED',
  payload,
});
export const clearDeviceIntegrationSetupAborted = () => ({
  type: 'CLEAR_DEVICE_INTEGRATION_SETUP_ABORTED',
});

export const updateContactProfile = (payload) => ({
  type: 'UPDATE_CONTACT_PROFILE_REQUESTED',
  payload,
});
const updateContactProfileSucceeded = (payload) => ({
  type: 'UPDATE_CONTACT_PROFILE_SUCCEEDED',
  payload,
});
const updateContactProfileFailed = (payload) => ({
  type: 'UPDATE_CONTACT_PROFILE_FAILED',
  payload,
});

export const syncDeviceIntegration = (
  provider: DeviceIntegrationProvider,
  opts?: SyncDeviceIntegrationOpts,
) => {
  const electronPlatform = apiPlatformToElectronPlatform(provider);

  if (
    window.desktopIntegrations?.supportedIntegrations?.find((i) => i.platform === electronPlatform)
  ) {
    window.desktopIntegrations.integration.sync(electronPlatform, opts);
  }
};

export const requestDeviceIntegrationAuthorization = (provider: DeviceIntegrationProvider) => {
  const electronPlatform = apiPlatformToElectronPlatform(provider);

  const integration = window.desktopIntegrations?.supportedIntegrations?.find(
    (i) => i.platform === electronPlatform,
  );
  if (integration?.requiresAuth) {
    window.desktopIntegrations.integration.authorize(electronPlatform);
  }
};

export const getInitialDesktopIntegrationAuthStatuses = (dispatch) =>
  window.desktopIntegrations.supportedIntegrations
    .filter(({ platform }) => platform !== 'apple-contacts')
    .forEach(({ platform }) =>
      window.desktopIntegrations.integration.isAuthorized(platform).then((authed) =>
        dispatch(
          setIntegrationAuthorization({
            provider: electronPlatformToAPIPlatform(platform),
            authed,
          }),
        ),
      ),
    );

export const updateProfileFromLinkedIn = (contactID: string, linkedinHandle: string) => {
  window.desktopIntegrations.integration.updateProfile({ contactID, linkedinHandle });
};

// Dispatch events to the Redux store in response to desktop integration events
export default function deviceIntegrationStateDispatch(dispatch) {
  window.desktopIntegrations.supportedIntegrations.forEach(({ platform }) => {
    const apiPlatform = electronPlatformToAPIPlatform(platform);

    window.desktopIntegrations.integration.onStatus(
      platform,
      (event: DesktopIntegrationStatusEvent | DesktopProfileUpdateStatusEvent) => {
        if (event.status === DesktopIntegrationStatusEventType.startingSync) {
          dispatch(integrationStartedSync({ provider: apiPlatform, ...event }));
        } else if (event.status === DesktopIntegrationStatusEventType.completedSync) {
          // will respond to this event in an epic that fetches the new data for the integration
          dispatch(integrationCompletedSync({ provider: apiPlatform, ...event }));
        } else if (event.status === DesktopIntegrationStatusEventType.auth) {
          dispatch(
            setIntegrationAuthorization({
              provider: apiPlatform,
              ...event,
            }),
          );
        } else if (event.status === DesktopIntegrationStatusEventType.progress) {
          dispatch(integrationSyncProgress({ provider: apiPlatform, ...event }));
        } else if (event.status === DesktopIntegrationStatusEventType.removed) {
          dispatch(integrationRemoved({ provider: apiPlatform }));
        } else if (event.status === DesktopIntegrationStatusEventType.aborted) {
          dispatch(integrationSetupAborted({ provider: apiPlatform }));
        } else if (event.status === DesktopIntegrationStatusEventType.error) {
          dispatch(integrationErrored({ provider: apiPlatform, ...event }));
        } else if (event.status === DesktopProfileUpdateStatusEventType.linkedInNeedsAuth) {
          dispatch(
            setIntegrationAuthorization({
              provider: 'linkedin',
              authed: false,
            }),
          );
        } else if (event.status === DesktopProfileUpdateStatusEventType.profileUpdateCompleted) {
          dispatch(
            updateContactProfileSucceeded({
              contactID: event.contactID,
              profile: event.profile,
              isPartial: event.isPartial,
            }),
          );
        } else if (event.status === DesktopProfileUpdateStatusEventType.profileUpdateError) {
          dispatch(updateContactProfileFailed({ contactID: event.contactID }));
        }
      },
    );
  });
}

// Perform side effects on the desktop integration state in response to Redux store updates
export const syncRemoteDeviceIntegrationChangesToDesktopApp = (_store) => (next) => (action) => {
  if (action.type === 'DELETE_INTEGRATION_SUCCEEDED') {
    const provider = apiPlatformToElectronPlatform(action.payload?.integration?.provider);
    if (
      provider &&
      window.desktopIntegrations?.supportedIntegrations?.find((i) => i.platform === provider)
    ) {
      window.desktopIntegrations.integration.remove(provider);
    }
  }

  // If there are any integrations that are setup in the desktop app but are not on the server,
  // remove them from the desktop app. We only do this on the first load because we can get into
  // a state during integration setup where the integration is added to the desktop app but is not
  // yet on the server.
  if (action.type === 'FETCH_ALL_INTEGRATIONS_SUCCEEDED' && action.payload?.isFirstLoad) {
    window.desktopIntegrations?.supportedIntegrations.forEach(({ platform }) => {
      const provider = electronPlatformToAPIPlatform(platform);

      window.desktopIntegrations.integration.isAuthorized(platform).then((authed) => {
        if (
          platform !== 'apple-contacts' &&
          authed &&
          !action.payload.response.find((i) => i.provider === provider)
        ) {
          window.desktopIntegrations.integration.remove(platform);
        }
      });
    });
  }

  if (action.type === 'UPDATE_CONTACT_PROFILE_REQUESTED') {
    window.desktopIntegrations?.integration.updateProfile(action.payload);
  }

  return next(action);
};
